import { motion, useReducedMotion } from "framer-motion";
import type { CSSProperties } from "react";

type Props = {
  /** 0..4 — drives wiggle speed, eye size, body color saturation */
  intensity?: 0 | 1 | 2 | 3 | 4;
  /** scale factor for the SVG */
  size?: number;
  /** if true, mouth animates "talking" */
  talking?: boolean;
  className?: string;
  style?: CSSProperties;
  /** decorative — defaults true; set false for landmark icons */
  ariaHidden?: boolean;
};

const BODY_COLORS: Record<number, string> = {
  0: "#FB7185",
  1: "#F43F5E",
  2: "#E11D48",
  3: "#BE123C",
  4: "#9F1239",
};

const FIN_COLORS: Record<number, string> = {
  0: "#FECDD3",
  1: "#FDA4AF",
  2: "#FB7185",
  3: "#F43F5E",
  4: "#E11D48",
};

export function MudCarpFish({
  intensity = 0,
  size = 1,
  talking = false,
  className,
  style,
  ariaHidden = true,
}: Props) {
  const reduced = useReducedMotion();

  const wiggleDuration = reduced ? 0 : 1.6 - intensity * 0.22;
  const bodyFill = BODY_COLORS[intensity];
  const finFill = FIN_COLORS[intensity];

  return (
    <motion.svg
      role={ariaHidden ? undefined : "img"}
      aria-label={ariaHidden ? undefined : "MudCarp"}
      aria-hidden={ariaHidden}
      viewBox="0 0 320 200"
      width={320 * size}
      height={200 * size}
      className={className}
      style={style}
      animate={
        reduced
          ? undefined
          : {
              rotate: [-2 - intensity, 2 + intensity, -2 - intensity],
              y: [0, -4 - intensity * 2, 0],
            }
      }
      transition={{
        duration: wiggleDuration || 0,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    >
      {/* Tail */}
      <motion.path
        d="M 30 100 L 0 60 L 10 100 L 0 140 Z"
        fill={finFill}
        stroke="#1A0A12"
        strokeWidth="4"
        strokeLinejoin="round"
        animate={
          reduced
            ? undefined
            : {
                rotate: [-15 - intensity * 3, 15 + intensity * 3, -15 - intensity * 3],
              }
        }
        transition={{
          duration: Math.max(0.4, wiggleDuration / 2),
          repeat: Infinity,
          ease: "easeInOut",
        }}
        style={{ transformOrigin: "30px 100px" }}
      />

      {/* Top fin */}
      <path
        d="M 130 50 L 170 30 L 200 60 Z"
        fill={finFill}
        stroke="#1A0A12"
        strokeWidth="4"
        strokeLinejoin="round"
      />

      {/* Bottom fin */}
      <path
        d="M 130 150 L 170 170 L 200 140 Z"
        fill={finFill}
        stroke="#1A0A12"
        strokeWidth="4"
        strokeLinejoin="round"
      />

      {/* Body */}
      <ellipse
        cx="170"
        cy="100"
        rx="140"
        ry="60"
        fill={bodyFill}
        stroke="#1A0A12"
        strokeWidth="5"
      />

      {/* Belly highlight */}
      <ellipse
        cx="170"
        cy="125"
        rx="110"
        ry="22"
        fill="#FFFBEF"
        opacity="0.55"
      />

      {/* Scales row 1 */}
      <g stroke="#1A0A12" strokeWidth="2.5" fill="none" opacity="0.9">
        <path d="M 90 80 q 12 -10 24 0" />
        <path d="M 116 80 q 12 -10 24 0" />
        <path d="M 142 80 q 12 -10 24 0" />
        <path d="M 168 80 q 12 -10 24 0" />
        <path d="M 194 80 q 12 -10 24 0" />
        <path d="M 220 80 q 12 -10 24 0" />
      </g>
      <g stroke="#1A0A12" strokeWidth="2.5" fill="none" opacity="0.7">
        <path d="M 100 100 q 12 -10 24 0" />
        <path d="M 126 100 q 12 -10 24 0" />
        <path d="M 152 100 q 12 -10 24 0" />
        <path d="M 178 100 q 12 -10 24 0" />
        <path d="M 204 100 q 12 -10 24 0" />
        <path d="M 230 100 q 12 -10 24 0" />
      </g>

      {/* Gill line */}
      <path
        d="M 220 60 q -10 40 0 80"
        stroke="#1A0A12"
        strokeWidth="4"
        fill="none"
        strokeLinecap="round"
      />

      {/* Eye socket */}
      <circle cx="265" cy="85" r="22" fill="#FFFBEF" stroke="#1A0A12" strokeWidth="4" />
      {/* Eyeball — bigger as intensity grows */}
      <motion.circle
        cx="270"
        cy="85"
        r={6 + intensity * 1.5}
        fill="#1A0A12"
        animate={
          reduced
            ? undefined
            : intensity >= 3
              ? { scale: [1, 1.25, 1] }
              : undefined
        }
        transition={{
          duration: 0.6,
          repeat: Infinity,
        }}
      />
      <circle cx="273" cy="82" r="2" fill="#FFFBEF" />

      {/* Eyebrow — only at high intensity */}
      {intensity >= 2 && (
        <path
          d="M 248 60 L 282 56"
          stroke="#1A0A12"
          strokeWidth={3 + intensity}
          strokeLinecap="round"
        />
      )}

      {/* Mouth */}
      <motion.path
        d={
          talking
            ? "M 300 100 q -10 12 -20 0"
            : intensity >= 2
              ? "M 300 100 q -12 -8 -20 0"
              : "M 300 100 q -10 4 -18 0"
        }
        stroke="#1A0A12"
        strokeWidth="5"
        fill={talking ? "#1A0A12" : "none"}
        strokeLinecap="round"
        strokeLinejoin="round"
        animate={
          reduced || !talking
            ? undefined
            : { d: ["M 300 100 q -10 12 -20 0", "M 300 100 q -10 2 -20 0", "M 300 100 q -10 12 -20 0"] }
        }
        transition={{ duration: 0.35, repeat: Infinity }}
      />

      {/* Speaker grille on belly — MudCarp is a fish-shaped device */}
      <g>
        <rect
          x="140"
          y="125"
          width="60"
          height="22"
          rx="2"
          fill="#1A0A12"
        />
        <g fill="#FACC15">
          <circle cx="148" cy="136" r="2" />
          <circle cx="158" cy="136" r="2" />
          <circle cx="168" cy="136" r="2" />
          <circle cx="178" cy="136" r="2" />
          <circle cx="188" cy="136" r="2" />
        </g>
      </g>

      {/* LED status dot */}
      <motion.circle
        cx="240"
        cy="55"
        r="4"
        fill={intensity >= 3 ? "#84CC16" : intensity >= 1 ? "#FACC15" : "#FB7185"}
        animate={reduced ? undefined : { opacity: [1, 0.3, 1] }}
        transition={{ duration: 1.2, repeat: Infinity }}
      />
    </motion.svg>
  );
}

export default MudCarpFish;
